import "./Login.css"
import { useState, useEffect } from "react"
import { LuUser, LuLock, LuMail } from "react-icons/lu"
import { useNavigate } from "react-router-dom"
import { auth, db } from "../firebase"
import {
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification
} from "firebase/auth"
import { doc, setDoc, getDoc } from "firebase/firestore"

function Signup() {
  const [imgLoaded, setImgLoaded] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [alert, setAlert] = useState(null)

  const navigate = useNavigate()
  const provider = new GoogleAuthProvider()

  const showAlert = (message, type = "info") => {
    setAlert({ message, type })
    setTimeout(() => setAlert(null), 3000)
  }

  const clearAlert = () => setAlert(null)

  const getFriendlyError = (code) => {
    switch (code) {
      case "auth/email-already-in-use":
        return "Email already registered"
      case "auth/invalid-email":
        return "Invalid email format"
      case "auth/weak-password":
        return "Password too weak (min 6 chars)"
      case "auth/popup-closed-by-user":
        return "Google popup closed"
      default:
        return "Signup failed. Try again"
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    const timer = setTimeout(() => setLoaded(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const createUserDocumentIfNotExists = async (user) => {
    const userRef = doc(db, "users", user.uid)
    const snap = await getDoc(userRef)

    if (!snap.exists()) {
      await setDoc(userRef, {
        name: user.displayName || "",
        email: user.email,
        branch: "",
        year: "",
        college: "",
        careerGoal: "",
        skillLevel: "",
        weeklyCommitment: "",
        screentime: {},
        totalScreenTime: 0,
        informationProvided: false,
        createdAt: new Date()
      })
    }
  }

  const handleSignup = async () => {
    if (!name || !email || !password || !confirmPassword) {
      showAlert("Fill all fields", "error")
      return
    }

    if (password !== confirmPassword) {
      showAlert("Passwords do not match", "error")
      return
    }

    setIsLoading(true)

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      )

      await updateProfile(userCredential.user, {
        displayName: name
      })

      await createUserDocumentIfNotExists(userCredential.user)

      await sendEmailVerification(userCredential.user)

      showAlert("Verification email sent!", "success")

      setTimeout(() => {
        navigate("/verify-email")
      }, 800)

    } catch (error) {
      showAlert(getFriendlyError(error.code), "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignup = async () => {
    setIsLoading(true)

    try {
      const result = await signInWithPopup(auth, provider)
      const user = result.user

      if (!user.displayName) {
        await updateProfile(user, {
          displayName: user.email.split("@")[0]
        })
      }

      await createUserDocumentIfNotExists(user)

      if (!user.emailVerified) {
        await sendEmailVerification(user)
        showAlert("Verification email sent!", "info")
        setTimeout(() => navigate("/verify-email"), 800)
      } else {
        showAlert("Login successful!", "success")
        setTimeout(() => navigate("/dashboard"), 600)
      }

    } catch (error) {
      showAlert(getFriendlyError(error.code), "error")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`login-wrapper ${loaded ? "loaded" : ""}`}>
      <div className="login-container">

        <div className="login-right">
          {!imgLoaded && <div className="image-skeleton"></div>}
          <img
            src="/assets/login2.png"
            alt="illustration"
            className={`login-image ${imgLoaded ? "show" : "hide"}`}
            onLoad={() => setImgLoaded(true)}
          />
        </div>

        <div className="login-left">
          <div className="login-content">

            <h1>Create Account</h1>

            {alert && (
              <div className={`alert ${alert.type}`}>
                <span className="alert-icon">
                  {alert.type === "success" ? "✓" : alert.type === "error" ? "✕" : "ℹ"}
                </span>
                <span className="alert-text">{alert.message}</span>
                <button className="alert-close" onClick={clearAlert}>×</button>
              </div>
            )}

            <div className="input-group">
              <LuUser className="input-icon" />
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div className="input-group">
              <LuMail className="input-icon" />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div className="input-group">
              <LuLock className="input-icon" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>

            <div className="input-group">
              <LuLock className="input-icon" />
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
            </div>

            <button
              className="login-btn"
              onClick={handleSignup}
              disabled={isLoading}
            >
              <div className="btn-with-loader">
                {isLoading && <div className="spinner"></div>}
                {isLoading ? "Creating..." : "Create Account"}
              </div>
            </button>

            <div className="signup-redirect">
              Already have an account?
              <span onClick={() => navigate("/login")}> Sign In</span>
            </div>

            

            

            <div className="divider">
              <span>Or continue with</span>
            </div>

            <div className="social-row">
              <button
                className="social-icon-btn google"
                onClick={handleGoogleSignup}
              >
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  className="google-logo"
                />
              </button>

              <button className="social-icon-btn apple">
                <img
                  src="https://www.apple.com/ac/structured-data/images/knowledge_graph_logo.png"
                  alt="Apple"
                  className="apple-logo"
                />
              </button>
            </div>

          </div>
        </div>

      </div>
    </div>
  )
}

export default Signup